import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import Button from '@material-ui/core/Button';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import MenuItem from '@material-ui/core/MenuItem';
import CustomDialog from 'components/CustomDialog/CustomDialog';
import { StyledTextField } from 'pages/co2Simulator/components/Item/styled';
import { ReduxState } from 'redux/types';
import { State } from 'types/Company';
import api from 'utils/api';
import snackbar from 'utils/snackbar';

interface Props {
    open: boolean;
    onClose: (companyId?: number) => void;
    companyId: number;
    oldState: State;
    handleAttributeChange: (attribute: string, value: any) => void;
}

const UpdateStateDialog = (props: Props) => {
    const { open, onClose, companyId, oldState, handleAttributeChange } = props;

    const allowedStates = useSelector((s: ReduxState) => s.user.allowedStates);

    const { t } = useTranslation();

    const [isLoading, setIsLoading] = useState(false);
    const [newState, setNewState] = useState<State>('prospect');

    useEffect(() => {
        if (!open) return;
        setNewState(oldState);
    }, [open, oldState]);

    const handleSubmit = useCallback(() => {
        setIsLoading(true);

        api.request('/companies/' + companyId + '/state', 'PUT', { state: newState }).then(res => {
            if (res.state === newState) {
                snackbar.success(t('dialogs.company_details.state_changed'));
                onClose();
            }
            else {
                snackbar.success(t('dialogs.company_details.state_change_error'));
            }
            handleAttributeChange('state', res.state);
        }).finally(() => {
            setIsLoading(false);
        });
    }, [companyId, newState, handleAttributeChange, onClose, t]);

    return (
        <CustomDialog onClose={() => onClose()} open={open} fullWidth maxWidth='xs'>
            <DialogTitle className='dialog-title-text-ellipsis'>
                {t('dialogs.update_state.title')}
            </DialogTitle>

            <DialogContent dividers>
                <StyledTextField
                    value={newState}
                    label={t('dialogs.update_state.state')}
                    variant='outlined'
                    onChange={(e) => setNewState(e.target.value as State)}
                    style={{ width: 'calc(100% - 12px)' }}
                    select
                >
                    {allowedStates.map((s: string) => {
                        return (
                            <MenuItem value={s} >
                                {t('dialogs.company_details.states.' + s)}
                            </MenuItem>
                        );
                    })}
                </StyledTextField>
            </DialogContent>

            <DialogActions>
                <Button onClick={() => onClose()} color='secondary' disabled={isLoading} >
                    {t('dialogs.generic.close')}
                </Button>

                <Button onClick={handleSubmit} color='primary' disabled={isLoading} >
                    {t('dialogs.generic.save')}
                </Button>
            </DialogActions>
        </CustomDialog>
    );
};

export default UpdateStateDialog;